import {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/** How long the tray marks what a removal returned, as the design pulses it. */
const MARKED_FOR_MS = 1700;

/**
 * One ask to tag a claim: the category the claim held when it was asked, and
 * when it was asked. A claim asked again starts its wait again, because a new
 * job runs.
 */
export interface TaggingAsk {
  formId: string;
  since: number;
}

interface Moments {
  returned: ReadonlySet<string>;
  markReturned: (positionIds: string[]) => void;
  /** The claims Praxis is tagging from their words, each with what it was asked. */
  tagging: ReadonlyMap<string, TaggingAsk>;
  markTagging: (claimId: string, formId: string) => void;
  settleTagging: (claimIds: readonly string[]) => void;
}

const MomentsContext = createContext<Moments>({
  returned: new Set(),
  markReturned: () => undefined,
  tagging: new Map(),
  markTagging: () => undefined,
  settleTagging: () => undefined,
});

/**
 * What just happened to the set that one part of the page does and another
 * shows: positions a removal gave back, for the tray to open on and mark, and
 * claims a write asked Praxis to tag, for their tags to say so. The moment is
 * the set's rather than either part's.
 */
export function ClaimSetMoments({ children }: { children: ReactNode }) {
  const [returned, setReturned] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const markReturned = useCallback((positionIds: string[]) => {
    setReturned(new Set(positionIds));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setReturned(new Set());
    }, MARKED_FOR_MS);
  }, []);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const [tagging, setTagging] = useState<ReadonlyMap<string, TaggingAsk>>(
    () => new Map(),
  );
  const markTagging = useCallback((claimId: string, formId: string) => {
    const since = Date.now();
    setTagging((held) => new Map(held).set(claimId, { formId, since }));
  }, []);
  const settleTagging = useCallback((claimIds: readonly string[]) => {
    setTagging((held) => {
      if (!claimIds.some((id) => held.has(id))) return held;
      const left = new Map(held);
      for (const id of claimIds) left.delete(id);
      return left;
    });
  }, []);
  return (
    <MomentsContext
      value={{ returned, markReturned, tagging, markTagging, settleTagging }}
    >
      {children}
    </MomentsContext>
  );
}

export function useClaimSetMoments() {
  return use(MomentsContext);
}
